import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import { ChevronRightIcon } from "@chakra-ui/icons";
import { FaYoutube, FaFacebook, FaLinkedin } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import FeatureCard from "../../components/ProductDetails/ProductFeatureCard";
import ServiceCard from "../../components/Common/ServiceCard";
import CommonFeatureCard from "../../components/Common/FeatureCard";
import CoCreationSection from "../../components/Common/CoCreationSection";

const QueueProPage = () => {
  const navigate = useNavigate();

  // Key Features Data
  const keyFeatures = [
    {
      icon: "/svg/q_feature_1.svg",
      title: "Smart Token Dispensing",
      description:
        "AI-powered kiosks that identify services and assign tokens based on user input, priority, and resource availability.",
    },
    {
      icon: "/svg/q_feature_2.svg",
      title: "Multi-Counter Queue Logic",
      description:
        "Dynamic counter allocation based on workload, agent availability, and real-time wait times.",
    },
    {
      icon: "/svg/q_feature_3.svg",
      title: "Multilingual Interface",
      description:
        "Supports multilingual interfaces on kiosks, displays, and customer notifications.",
    },
    {
      icon: "/svg/q_feature_4.svg",
      title: "Digital Signage and Calling Displays",
      description:
        "Seamless integration with LED and TV-based display units to show token status, instructions, and public messages.",
    },
    {
      icon: "/svg/q_feature_5.svg",
      title: "AI-Driven Customer Routing",
      description:
        "AdAdaptive service queue logic to reduce congestion and improve average service time.",
    },
    {
      icon: "/svg/q_feature_6.svg",
      title: "Integrated Feedback Module",
      description:
        "Capture post-service feedback directly from kiosks or via SMS based surveys.",
    },
    {
      icon: "/svg/product_grid.svg",
      title: "Admin Dashboard",
      description:
        "Real-time monitoring, performance tracking, and reporting dashboard for supervisors and department heads.",
    },
  ];

  // Add-ons and Integration Data
  const addOnsIntegration = [
    {
      id: 1,
      title: "3rd Party Integration",
      description:
        "Compatible with Internal systems, SMS gateways, mobile apps, and payment gateways.",
    },
    {
      id: 2,
      title: "Appointment Booking",
      description:
        "Supports pre-booked appointments and hybrid queue options (walk-in + scheduled).",
    },
    {
      id: 3,
      title: "CRM and Helpdesk Integration",
      description:
        "Can connect with your existing Customer Relationship Management and Ticketing Systems.",
    },
    {
      id: 4,
      title: "Custom",
      description: "Custom made kiosk options are available.",
    },
  ];

  // Benefits Data
  const benefits = [
    {
      id: 1,
      title: "Reduce Wait Time",
      description:
        "Intelligent routing and load balancing decrease average wait time.",
      icon: "/svg/q_benefit_1.svg",
      bgColor: "#E3F2FD",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Improve Customer Satisfaction",
      description:
        "Personalized service and reduced confusion enhance user trust and experience.",
      icon: "/svg/q_benefit_2.svg",
      bgColor: "#E8F5E8",
      iconColor: "#2E7D32",
    },
    {
      id: 3,
      title: "Boost Staff Efficiency",
      description:
        "Frontline staff can focus on service delivery rather than crowd management.",
      icon: "/svg/frame_5.svg",
      bgColor: "#FFF3E0",
      iconColor: "#F57C00",
    },
    {
      id: 4,
      title: "Ensure Transparency",
      description:
        "Public display of queue movement and time estimation fosters accountability.",
      icon: "/svg/q_benefit_4.svg",
      bgColor: "#FCE4EC",
      iconColor: "#C2185B",
    },
    {
      id: 5,
      title: "Enhance Reporting",
      description:
        "Use data insights to measure service efficiency, staff performance, and visitor trends.",
      icon: "/svg/q_benefit_5.svg",
      bgColor: "#F3E5F5",
      iconColor: "#7B1FA2",
    },
  ];

  // Deployment Options Data
  const deploymentOptions = [
    {
      id: 1,
      title: "Government agencies",
      description:
        "Serve citizens efficiently with scalable installations across service desks.",
    },
    {
      id: 2,
      title: "Financial institutions",
      description: "Streamline customer flow in busy bank branches.",
    },
    {
      id: 3,
      title: "Hospitals & Clinics",
      description: "Manage outpatient queues with medical priority support.",
    },
    {
      id: 4,
      title: "Corporate Offices & Utilities",
      description: "Optimize high-volume customer interactions with ease.",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Queue Pro | AI-Powered Queue Management System for Government & Enterprise</title>
        <meta 
          name="description" 
          content="Discover Queue Pro — Bangladesh's leading queue management platform powered by AI. Streamline citizen services, reduce wait times, and enhance customer experience with multilingual, scalable, and token-based flow systems." 
        />
        <meta 
          name="keywords" 
          content="Queue management system Bangladesh, AI-powered queue software Bangladesh, customer flow management Bangladesh, token system for government services, smart kiosk queue solution Bangladesh, public service automation Bangladesh, citizen service platform, queue system for hospitals and banks, multilingual token software Bangladesh, digital customer experience tools, queue management for donor-funded projects, eGovernment service queue platform, smart citizen engagement system Bangladesh" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/products/queuepro" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/products/queuepro" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Queue Pro | AI-Powered Queue Management System for Government & Enterprise" />
        <meta property="og:description" content="Discover Queue Pro — Bangladesh's leading queue management platform powered by AI. Streamline citizen services, reduce wait times, and enhance customer experience with multilingual, scalable, and token-based flow systems." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/products/queuepro" />
        <meta name="twitter:title" content="Queue Pro | AI-Powered Queue Management System for Government & Enterprise" />
        <meta name="twitter:description" content="Discover Queue Pro — Bangladesh's leading queue management platform powered by AI. Streamline citizen services, reduce wait times, and enhance customer experience with multilingual, scalable, and token-based flow systems." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <Box
        py={6}
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
        }}
      >
      {/* Breadcrumb Section */}
      <Box>
        <Container maxW="8xl" px={{ base: 4, md: 6 }}>
          <Breadcrumb
            spacing="8px"
            separator={<ChevronRightIcon color="gray.500" />}
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                color="#000B8C"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate("/")}
                cursor="pointer"
              >
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <Text color="gray.600">Queue-Pro</Text>
            </BreadcrumbItem>
          </Breadcrumb>
        </Container>
      </Box>

      <Container maxW="7xl" py={{ base: 4, md: 6 }}>
        <HStack align="center" wrap="wrap">
          <VStack
            align="start"
            spacing={0}
            pb={2}
            flex={{ base: 1, lg: 3 }}
            minW={{ base: "100%", md: "500px", lg: "700px" }}
          >
            <Image
              src="/png/queue-pro.png"
              alt="Queue-Pro Logo"
              height={{ base: "60px", md: "80px" }}
              objectFit="contain"
              loading="lazy"
              fallbackSrc="/png/ossp_logo.png"
            />

            <Heading
              as="h1"
              pt={4}
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="#000B8C"
              lineHeight="1.2"
            >
              Queue-Pro –{" "}
              <Text as="span" color="gray.700">
                Customer Flow Management System
              </Text>
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.600"
              mt={2}
              fontWeight="medium"
              fontStyle={"italic"}
            >
              Efficient, Intelligent, and Scalable Service Delivery
            </Text>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.700"
              lineHeight="1.2"
              mt={4}
              textAlign="justify"
              maxW={{ base: "100%", md: "700px", lg: "800px" }}
            >
              In today’s fast-paced world, long queues and disorganized service
              counters are not just inconvenient—they impact citizen
              satisfaction, staff productivity, and institutional credibility.
              Queue-Pro, developed by Business Automation Limited, is Bangladesh’s
              leading customer flow management solution, trusted by government
              offices, banks, hospitals, and service centers nationwide.
            </Text>
            <Box h={4} />
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.700"
              lineHeight="1.2"
              textAlign="justify"
              maxW={{ base: "100%", md: "700px", lg: "800px" }}
            >
              From citizen service desks to private customer care units, Queue
              Pro transforms waiting areas into smart, data-driven service
              points. Built with AI-powered logic, multilingual displays,
              touchless kiosks, and performance analytics, Queue-Pro ensures
              that every customer receives timely service, improves their
              experience, and enhances operational efficiency.
            </Text>
          </VStack>

          <Box
            flex={{ base: 1, lg: 1 }}
            minW={{ base: "100%", md: "350px" }}
            maxW={{ base: "100%", md: "300px" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="100%"
          >
            <Image
              src="/png/queuepro_product_img.png"
              alt="Queue-Pro Illustration"
              maxW="100%"
              height="auto"
              borderRadius="lg"
              loading="lazy"
              fallbackSrc="/png/default-product.png"
            />
          </Box>
        </HStack>
      </Container>

      {/* Key Features Section */}
      <Container maxW="7xl" py={{ base: 8, md: 12 }}>
        <VStack spacing={8} align="center">
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="#000B8C"
            lineHeight="1.2"
          >
            Key{" "}
            <Text as="span" color="gray.700">
              Features
            </Text>
          </Heading>

          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={6}
            w="100%"
            mt={8}
            justifyItems="center"
          >
            {keyFeatures.map((feature, index) => (
              <GridItem
                key={index}
                w="100%"
                maxW="400px"
                colSpan={
                  index === keyFeatures.length - 1 &&
                  keyFeatures.length % 3 === 1 &&
                  keyFeatures.length > 3
                    ? { base: 1, md: 2, lg: 3 }
                    : 1
                }
                justifySelf={
                  index === keyFeatures.length - 1 &&
                  keyFeatures.length % 3 === 1 &&
                  keyFeatures.length > 3
                    ? "center"
                    : "stretch"
                }
              >
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              </GridItem>
            ))}
          </Grid>
        </VStack>
      </Container>

      {/* Queue Management Devices Image Section */}
      <Box py={{ base: 12, md: 16 }} position="relative" overflow="hidden">
        <Container maxW="7xl">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            w="100%"
          >
            <Image
              src="/png/queuePro.png"
              alt="Queue Management Devices"
              maxW={{ base: "90%", md: "900px" }}
              w="100%"
              borderRadius="lg"
              loading="lazy"
              fallbackSrc="/png/queuePro.png"
            />
          </Box>
        </Container>
      </Box>

      {/* Add-ons and Integration Section */}
      <CommonFeatureCard
        title="Add-ons and Integration"
        features={addOnsIntegration}
      />

      {/* Benefits Section */}
      <Container maxW="7xl" py={{ base: 12, md: 20 }}>
        <VStack spacing={8} align="center">
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="#000B8C"
            textAlign="center"
          >
            Benefits
          </Heading>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={6}
            w="100%"
            mt={8}
            justifyContent="center"
            alignItems="stretch"
          >
            {benefits.map((benefit) => (
              <Box
                key={benefit.id}
                w="100%"
                maxW="400px"
                minW={{ base: "100%", md: "300px" }}
                flex={{
                  base: "1 1 100%",
                  md: "1 1 calc(50% - 12px)",
                  lg: "1 1 calc(33.333% - 16px)",
                }}
              >
                <ServiceCard service={benefit} />
              </Box>
            ))}
          </Box>
        </VStack>
      </Container>

      {/* Deployment Options Section */}
      <CoCreationSection
        blueTitle={"Deployment "}
        blackTitle={"Options"}
        title="Deployment Options"
        subtitle="Queue-Pro is available as a cloud-based SaaS model or on-premise deployment, tailored to your security, scale, and policy needs."
        processes={deploymentOptions}
        illustrationImage="/png/queuepro_asset.png"
        footerText=""
      />

      {/* Additional Information Section */}
      <Container maxW="7xl" py={8}>
        <Box textAlign="center">
          <VStack spacing={4} align="center">
            {/* Social Media Links */}
            <HStack spacing={4} align="center">
              <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
                Watch, Learn, Grow:
              </Text>

              {/* YouTube Link */}
              <Box
                as="a"
                href="https://www.youtube.com/@Queue-Pro"
                target="_blank"
                rel="noopener noreferrer"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
                bg="red.500"
                borderRadius="md"
                color="white"
                _hover={{
                  bg: "red.600",
                  transform: "scale(1.05)",
                }}
                transition="all 0.2s ease"
                cursor="pointer"
              >
                <FaYoutube size="20px" />
              </Box>

              {/* Facebook Link */}
              <Box
                as="a"
                href="https://www.facebook.com/queueprokiosk"
                target="_blank"
                rel="noopener noreferrer"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
                bg="blue.600"
                borderRadius="md"
                color="white"
                _hover={{
                  bg: "blue.700",
                  transform: "scale(1.05)",
                }}
                transition="all 0.2s ease"
                cursor="pointer"
              >
                <FaFacebook size="20px" />
              </Box>

              {/* LinkedIn Link */}
              <Box
                as="a"
                href="https://bd.linkedin.com/company/queue-pro"
                target="_blank"
                rel="noopener noreferrer"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
                bg="blue.700"
                borderRadius="md"
                color="white"
                _hover={{
                  bg: "blue.800",
                  transform: "scale(1.05)",
                }}
                transition="all 0.2s ease"
                cursor="pointer"
              >
                <FaLinkedin size="20px" />
              </Box>
            </HStack>

            {/* Website Link */}
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              lineHeight="1.6"
            >
              For more details, please visit{" "}
              <Text
                as="a"
                href="https://queue-pro.com/"
                target="_blank"
                rel="noopener noreferrer"
                color="#000B8C"
                textDecoration="underline"
                fontWeight="medium"
                _hover={{
                  color: "blue.700",
                  textDecoration: "none",
                }}
              >
                www.queue-pro.com
              </Text>
            </Text>
          </VStack>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default QueueProPage;
